import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { submitApplication, getUserApplication } from "../api/educentialApi";
import UploadModal from "../components/UploadModal";
import {
  ArrowLeft,
  ArrowRight,
  Check,
  Upload,
  FileCheck,
  AlertCircle,
  Briefcase,
  GraduationCap,
  Globe,
  FileSpreadsheet,
  FileText,
  CheckCircle2
} from "lucide-react";

export default function DocumentSubmission() {

  const [modalOpen, setModalOpen] = useState(false);
  const [modalTitle, setModalTitle] = useState("");
  const [activeUploadField, setActiveUploadField] = useState(null);

  const [currentStep, setCurrentStep] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState("");
  const [success, setSuccess] = useState(false);
  const [refId, setRefId] = useState("");

  const [uploadedNames, setUploadedNames] = useState({});

  const [formData, setFormData] = useState({
    personalInfo: {
      fullName: "",
      email: "",
      dateOfBirth: "",
      gender: "",
      nationality: "",
      countryOfResidence: "",
      passportNumber: "",
      phoneNumber: "",
      passportBioPageFileId: "",
    },
    academicDocuments: {
      degreeCertificateFileId: "",
      transcriptsFileId: "",
      waecNecoFileId: "",
      referenceLetterFileId: "",
      personalStatementFileId: "",
      researchProposalFileId: "",
      nameChangeAffidavitFileId: "",
      birthCertificateFileId: "",
    },
    englishProficiency: {
      waecNecoEnglishFileId: "",
      ieltsToeflPteFileId: "",
      moiLetterFileId: "",
    },
    employmentSponsor: {
      currentlyEmployed: false,
      employmentLetterFileId: "",
      sponsorName: "",
      sponsorIdFileId: "",
    },
    visaSupport: {
      passportFullPageFileId: "",
      dependentBirthCertFileId: "",
      marriageCertFileId: "",
      relationshipProofFileId: "",
      nameDiscrepancyAffidavitFileId: "",
      proofOfFundsFileId: "",
      tbTestCertFileId: "",
      policeClearanceCertFileId: "",
    },
    consent: {
      confirmAll: false,
      consentUseDocuments: false,
    }
  });

  const stepsList = [
    { num: 1, name: "Personal Info", icon: Globe },
    { num: 2, name: "Academic Docs", icon: GraduationCap },
    { num: 3, name: "English Proficiency", icon: FileText },
    { num: 4, name: "Employment & Sponsor", icon: Briefcase },
    { num: 5, name: "Visa Support", icon: FileSpreadsheet },
    { num: 6, name: "Final Consent", icon: FileCheck },
  ];

  // Load existing application data on mount
  useEffect(() => {
    const loadExistingApplication = async () => {
      try {
        const existingApp = await getUserApplication("UK");

        if (existingApp) {
          // Populate form with existing data
          setFormData({
            personalInfo: existingApp.personalInfo || {
              fullName: "",
              email: "",
              dateOfBirth: "",
              gender: "",
              nationality: "",
              countryOfResidence: "",
              passportNumber: "",
              phoneNumber: "",
              passportBioPageFileId: "",
            },
            academicDocuments: existingApp.academicDocuments || {
              degreeCertificateFileId: "",
              transcriptsFileId: "",
              waecNecoFileId: "",
              referenceLetterFileId: "",
              personalStatementFileId: "",
              researchProposalFileId: "",
              nameChangeAffidavitFileId: "",
              birthCertificateFileId: "",
            },
            englishProficiency: existingApp.englishProficiency || {
              waecNecoEnglishFileId: "",
              ieltsToeflPteFileId: "",
              moiLetterFileId: "",
            },
            employmentSponsor: existingApp.employmentSponsor || {
              currentlyEmployed: false,
              employmentLetterFileId: "",
              sponsorName: "",
              sponsorIdFileId: "",
            },
            visaSupport: existingApp.visaSupport || {
              passportFullPageFileId: "",
              dependentBirthCertFileId: "",
              marriageCertFileId: "",
              relationshipProofFileId: "",
              nameDiscrepancyAffidavitFileId: "",
              proofOfFundsFileId: "",
              tbTestCertFileId: "",
              policeClearanceCertFileId: "",
            },
            consent: existingApp.consent || {
              confirmAll: false,
              consentUseDocuments: false,
            },
          });

          // Populate uploaded file names
          const allFileIds = {
            ...existingApp.personalInfo,
            ...existingApp.academicDocuments,
            ...existingApp.englishProficiency,
            ...existingApp.employmentSponsor,
            ...existingApp.visaSupport,
          };

          const uploadedFileNames = {};
          Object.entries(allFileIds).forEach(([key, value]) => {
            if (key.includes('FileId') && value) {
              uploadedFileNames[key] = value;
            }
          });
          setUploadedNames(uploadedFileNames);
        }
      } catch (error) {
        // If no application found (404), that's fine - user is starting fresh
        if (error.response?.status !== 404) {
          console.error('Failed to load existing application:', error);
        }
      }
    };

    loadExistingApplication();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleInputChange = (section, field, value) => {
    setFormData(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: value
      }
    }));
  };

  const triggerUpload = (field, label) => {
    setActiveUploadField(field);
    setModalTitle(label);
    setModalOpen(true);
  };

  const handleUploadSuccess = (fileId, filename, url) => {
    let section = "";
    if (activeUploadField in formData.personalInfo) section = "personalInfo";
    else if (activeUploadField in formData.academicDocuments) section = "academicDocuments";
    else if (activeUploadField in formData.englishProficiency) section = "englishProficiency";
    else if (activeUploadField in formData.employmentSponsor) section = "employmentSponsor";
    else if (activeUploadField in formData.visaSupport) section = "visaSupport";

    if (section) {
      handleInputChange(section, activeUploadField, fileId);
      setUploadedNames(prev => ({
        ...prev,
        [activeUploadField]: filename
      }));
    }
  };

  const isStepValid = () => {
    switch (currentStep) {
      case 1:
        const { fullName, email, dateOfBirth, gender, nationality, countryOfResidence, passportNumber, phoneNumber, passportBioPageFileId } = formData.personalInfo;
        return !!(fullName && email && dateOfBirth && gender && nationality && countryOfResidence && passportNumber && phoneNumber && passportBioPageFileId);
      case 2:
        const { degreeCertificateFileId, transcriptsFileId, waecNecoFileId, referenceLetterFileId, personalStatementFileId } = formData.academicDocuments;
        return !!(degreeCertificateFileId && transcriptsFileId && waecNecoFileId && referenceLetterFileId && personalStatementFileId);
      case 3:
        const { waecNecoEnglishFileId, ieltsToeflPteFileId, moiLetterFileId } = formData.englishProficiency;
        return !!(waecNecoEnglishFileId || ieltsToeflPteFileId || moiLetterFileId);
      case 4:
        const { currentlyEmployed, employmentLetterFileId, sponsorName, sponsorIdFileId } = formData.employmentSponsor;
        if (currentlyEmployed && !employmentLetterFileId) return false;
        return !!(sponsorName && sponsorIdFileId);
      case 5:
        const { passportFullPageFileId, proofOfFundsFileId } = formData.visaSupport;
        return !!(passportFullPageFileId && proofOfFundsFileId);
      case 6:
        return formData.consent.confirmAll && formData.consent.consentUseDocuments;
      default:
        return false;
    }
  };

  const handleNext = () => {
    if (isStepValid()) {
      setFormError("");
      setCurrentStep(prev => Math.min(prev + 1, 6));
    } else {
      setFormError("Please fill out all required fields and upload the mandatory files before moving to the next step.");
    }
  };

  const handlePrev = () => {
    setFormError("");
    setCurrentStep(prev => Math.max(prev - 1, 1));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError("");

    if (!isStepValid()) {
      setFormError("You must accept all declarations and consents before submitting your application.");
      return;
    }

    setSubmitting(true);
    try {
      const response = await submitApplication("UK", formData);
      if (response && response.applicationId) {
        setRefId(response.applicationId);
        setSuccess(true);
      } else {
        throw new Error("Unable to parse application response.");
      }
    } catch (err) {
      console.error("Submission failed:", err);
      const errMsg = err.response?.data?.error || err.message || "Failed to submit application. Please try again.";
      setFormError(errMsg);
    } finally {
      setSubmitting(false);
    }
  };

  const renderUploadBox = (field, label, required = true) => {
    const isUploaded = !!uploadedNames[field];
    return (
      <div className="space-y-2">
        <div className="flex justify-between items-center">
          <label className="block text-gray-700 text-sm font-semibold">
            {label}
          </label>
          {required ? (
            <span className="text-yellow-600 text-xs font-medium">Required</span>
          ) : (
            <span className="text-gray-400 text-xs font-medium">Optional</span>
          )}
        </div>

        {isUploaded ? (
          <div className="flex justify-between items-center p-3 bg-green-50 border border-green-200 rounded-lg">
            <div className="flex items-center space-x-2 min-w-0">
              <Check className="w-5 h-5 text-green-600 shrink-0" />
              <span className="text-sm font-medium text-gray-900 truncate max-w-[200px]">
                {uploadedNames[field]}
              </span>
            </div>
            <button
              type="button"
              onClick={() => triggerUpload(field, label)}
              className="text-xs text-yellow-600 hover:text-yellow-700 font-semibold shrink-0"
            >
              Replace
            </button>
          </div>
        ) : (
          <button
            type="button"
            onClick={() => triggerUpload(field, label)}
            className="w-full flex items-center justify-center space-x-2 p-3 bg-white hover:bg-gray-50 border-2 border-dashed border-gray-300 hover:border-yellow-500 rounded-lg transition-all group"
          >
            <Upload className="w-4 h-4 text-gray-400 group-hover:text-yellow-600 transition-colors" />
            <span className="text-sm font-medium text-gray-600 group-hover:text-gray-900 transition-colors">
              Choose Document
            </span>
          </button>
        )}
      </div>
    );
  };

  if (success) {
    return (
      <div className="bg-gray-50 min-h-full flex flex-col justify-center items-center px-4 py-12">
        <div className="w-full max-w-lg bg-white border border-gray-200 rounded-xl p-8 md:p-10 shadow-sm text-center">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle2 className="w-10 h-10 text-green-600" />
          </div>

          <h2 className="text-2xl font-bold text-gray-900 mb-3">
            Application Submitted Successfully
          </h2>
          <p className="text-gray-600 text-sm leading-relaxed mb-6">
            Your UK study application has been successfully submitted. Our team will review your documents and contact you shortly.
          </p>

          <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mb-8">
            <div className="text-xs text-gray-500 font-semibold uppercase mb-1">
              Application Reference ID
            </div>
            <div className="text-xl font-bold text-gray-900 font-mono">
              {refId}
            </div>
          </div>

          <div className="flex flex-col sm:flex-row space-y-3 sm:space-y-0 sm:space-x-4">
            <Link
              to="/dashboard"
              className="flex-1 py-3 bg-yellow-500 hover:bg-yellow-600 text-white rounded-lg font-semibold text-sm transition-colors flex items-center justify-center"
            >
              Go to Dashboard
            </Link>
            <Link
              to="/dashboard/progress"
              className="flex-1 py-3 bg-white hover:bg-gray-50 border border-gray-300 text-gray-700 rounded-lg text-sm font-semibold transition-colors flex items-center justify-center"
            >
              Track Progress
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gray-50 min-h-full">
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <Link
            to="/dashboard"
            className="inline-flex items-center space-x-2 text-gray-600 hover:text-gray-900 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            <span className="text-sm font-medium">Back to Dashboard</span>
          </Link>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          {/* Sidebar Progress */}
          <div className="lg:col-span-1">
            <div className="bg-white border border-gray-200 rounded-lg p-6 sticky top-8">
              <div className="mb-6">
                <h2 className="text-lg font-bold text-gray-900 mb-1">
                  UK Study Application
                </h2>
                <p className="text-gray-500 text-sm">
                  Complete all steps to submit your application
                </p>
              </div>

              <div className="space-y-3">
                {stepsList.map((step) => {
                  const isActive = currentStep === step.num;
                  const isCompleted = currentStep > step.num;
                  const Icon = step.icon;

                  return (
                    <div
                      key={step.num}
                      className={`flex items-center space-x-3 p-3 rounded-lg transition-all ${
                        isActive
                          ? "bg-yellow-50 border border-yellow-200"
                          : isCompleted
                          ? "bg-green-50 border border-green-200"
                          : "bg-gray-50 border border-gray-200"
                      }`}
                    >
                      <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${
                        isActive
                          ? "bg-yellow-500 text-white"
                          : isCompleted
                          ? "bg-green-500 text-white"
                          : "bg-white border border-gray-300 text-gray-400"
                      }`}>
                        {isCompleted ? <Check className="w-4 h-4" /> : <Icon className="w-4 h-4" />}
                      </div>
                      <div className="min-w-0">
                        <div className={`text-sm font-semibold ${
                          isActive ? "text-yellow-900" : isCompleted ? "text-green-900" : "text-gray-500"
                        }`}>
                          {step.name}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Main Form */}
          <div className="lg:col-span-3">
            <div className="bg-white border border-gray-200 rounded-lg p-6 md:p-8">
              {formError && (
                <div className="mb-6 flex items-start space-x-3 bg-red-50 border border-red-200 text-red-700 p-4 rounded-lg text-sm">
                  <AlertCircle className="w-5 h-5 shrink-0 mt-0.5" />
                  <span>{formError}</span>
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-8">

                {/* Step 1: Personal Info */}
                {currentStep === 1 && (
                  <div className="space-y-6">
                    <div className="pb-4 border-b border-gray-200">
                      <h3 className="text-xl font-bold text-gray-900 mb-1">
                        Personal Information
                      </h3>
                      <p className="text-gray-600 text-sm">
                        Fill in your identity details exactly as they appear on your passport.
                      </p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="space-y-2">
                        <label className="block text-gray-700 text-sm font-semibold">
                          Full Legal Name
                        </label>
                        <input
                          type="text"
                          value={formData.personalInfo.fullName}
                          onChange={(e) => handleInputChange("personalInfo", "fullName", e.target.value)}
                          className="w-full px-4 py-3 bg-white border border-gray-300 rounded-lg text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:border-transparent transition-all text-sm"
                          placeholder="John Doe"
                          required
                        />
                      </div>

                      <div className="space-y-2">
                        <label className="block text-gray-700 text-sm font-semibold">
                          Email Address
                        </label>
                        <input
                          type="email"
                          value={formData.personalInfo.email}
                          onChange={(e) => handleInputChange("personalInfo", "email", e.target.value)}
                          className="w-full px-4 py-3 bg-white border border-gray-300 rounded-lg text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:border-transparent transition-all text-sm"
                          placeholder="john@example.com"
                          required
                        />
                      </div>

                      <div className="space-y-2">
                        <label className="block text-gray-700 text-sm font-semibold">
                          Date of Birth
                        </label>
                        <input
                          type="date"
                          value={formData.personalInfo.dateOfBirth}
                          onChange={(e) => handleInputChange("personalInfo", "dateOfBirth", e.target.value)}
                          className="w-full px-4 py-3 bg-white border border-gray-300 rounded-lg text-gray-900 focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:border-transparent transition-all text-sm"
                          required
                        />
                      </div>

                      <div className="space-y-2">
                        <label className="block text-gray-700 text-sm font-semibold">
                          Gender
                        </label>
                        <select
                          value={formData.personalInfo.gender}
                          onChange={(e) => handleInputChange("personalInfo", "gender", e.target.value)}
                          className="w-full px-4 py-3 bg-white border border-gray-300 rounded-lg text-gray-900 focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:border-transparent transition-all text-sm"
                          required
                        >
                          <option value="" disabled>Select Gender</option>
                          <option value="MALE">Male</option>
                          <option value="FEMALE">Female</option>
                          <option value="OTHER">Other</option>
                        </select>
                      </div>

                      <div className="space-y-2">
                        <label className="block text-gray-700 text-sm font-semibold">
                          Nationality
                        </label>
                        <input
                          type="text"
                          value={formData.personalInfo.nationality}
                          onChange={(e) => handleInputChange("personalInfo", "nationality", e.target.value)}
                          className="w-full px-4 py-3 bg-white border border-gray-300 rounded-lg text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:border-transparent transition-all text-sm"
                          placeholder="Nigerian"
                          required
                        />
                      </div>

                      <div className="space-y-2">
                        <label className="block text-gray-700 text-sm font-semibold">
                          Country of Residence
                        </label>
                        <input
                          type="text"
                          value={formData.personalInfo.countryOfResidence}
                          onChange={(e) => handleInputChange("personalInfo", "countryOfResidence", e.target.value)}
                          className="w-full px-4 py-3 bg-white border border-gray-300 rounded-lg text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:border-transparent transition-all text-sm"
                          placeholder="Nigeria"
                          required
                        />
                      </div>

                      <div className="space-y-2">
                        <label className="block text-gray-700 text-sm font-semibold">
                          Passport Number
                        </label>
                        <input
                          type="text"
                          value={formData.personalInfo.passportNumber}
                          onChange={(e) => handleInputChange("personalInfo", "passportNumber", e.target.value)}
                          className="w-full px-4 py-3 bg-white border border-gray-300 rounded-lg text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:border-transparent transition-all text-sm"
                          placeholder="A00000000"
                          required
                        />
                      </div>

                      <div className="space-y-2">
                        <label className="block text-gray-700 text-sm font-semibold">
                          Phone Number
                        </label>
                        <input
                          type="tel"
                          value={formData.personalInfo.phoneNumber}
                          onChange={(e) => handleInputChange("personalInfo", "phoneNumber", e.target.value)}
                          className="w-full px-4 py-3 bg-white border border-gray-300 rounded-lg text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:border-transparent transition-all text-sm"
                          placeholder="+234..."
                          required
                        />
                      </div>
                    </div>

                    <div className="pt-4 border-t border-gray-200">
                      {renderUploadBox("passportBioPageFileId", "Passport Bio Page Scan")}
                    </div>
                  </div>
                )}

                {/* Step 2: Academic Docs */}
                {currentStep === 2 && (
                  <div className="space-y-6">
                    <div className="pb-4 border-b border-gray-200">
                      <h3 className="text-xl font-bold text-gray-900 mb-1">
                        Academic Credentials
                      </h3>
                      <p className="text-gray-600 text-sm">
                        Upload all credentials proving your high school or tertiary qualifications.
                      </p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      {renderUploadBox("degreeCertificateFileId", "Degree Certificate")}
                      {renderUploadBox("transcriptsFileId", "Official Academic Transcripts")}
                      {renderUploadBox("waecNecoFileId", "O'Level Result Sheet")}
                      {renderUploadBox("referenceLetterFileId", "Reference Letter")}
                      {renderUploadBox("personalStatementFileId", "Statement of Purpose")}
                      {renderUploadBox("researchProposalFileId", "Research Proposal (PhD only)", false)}
                      {renderUploadBox("birthCertificateFileId", "Birth Certificate", false)}
                      {renderUploadBox("nameChangeAffidavitFileId", "Name Change Affidavit", false)}
                    </div>
                  </div>
                )}

                {/* Step 3: English Proficiency */}
                {currentStep === 3 && (
                  <div className="space-y-6">
                    <div className="pb-4 border-b border-gray-200">
                      <h3 className="text-xl font-bold text-gray-900 mb-1">
                        English Proficiency Evidence
                      </h3>
                      <p className="text-gray-600 text-sm">
                        Upload evidence that satisfies university English requirements. Provide at least one document.
                      </p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      {renderUploadBox("waecNecoEnglishFileId", "WAEC English Result", false)}
                      {renderUploadBox("ieltsToeflPteFileId", "IELTS/TOEFL/PTE Score Report", false)}
                      {renderUploadBox("moiLetterFileId", "Medium of Instruction Letter", false)}
                    </div>
                  </div>
                )}

                {/* Step 4: Employment & Sponsor */}
                {currentStep === 4 && (
                  <div className="space-y-6">
                    <div className="pb-4 border-b border-gray-200">
                      <h3 className="text-xl font-bold text-gray-900 mb-1">
                        Employment & Financial Sponsor
                      </h3>
                      <p className="text-gray-600 text-sm">
                        Provide details about your sponsor and employment status.
                      </p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="space-y-2 md:col-span-2">
                        <label className="flex items-center space-x-3 p-4 bg-gray-50 border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-100 transition-colors">
                          <input
                            type="checkbox"
                            checked={formData.employmentSponsor.currentlyEmployed}
                            onChange={(e) => handleInputChange("employmentSponsor", "currentlyEmployed", e.target.checked)}
                            className="w-5 h-5 rounded border-gray-300 text-yellow-500 focus:ring-yellow-500"
                          />
                          <div>
                            <span className="text-sm font-semibold text-gray-900 block">I am currently employed</span>
                            <span className="text-gray-500 text-xs">Check this if you are actively working</span>
                          </div>
                        </label>
                      </div>

                      <div className="space-y-2 md:col-span-2">
                        <label className="block text-gray-700 text-sm font-semibold">
                          Sponsor's Full Name or Organization
                        </label>
                        <input
                          type="text"
                          value={formData.employmentSponsor.sponsorName}
                          onChange={(e) => handleInputChange("employmentSponsor", "sponsorName", e.target.value)}
                          className="w-full px-4 py-3 bg-white border border-gray-300 rounded-lg text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:border-transparent transition-all text-sm"
                          placeholder="Parent Name, Organization, Self, etc"
                          required
                        />
                      </div>

                      {formData.employmentSponsor.currentlyEmployed && (
                        renderUploadBox("employmentLetterFileId", "Employment Letter")
                      )}

                      {renderUploadBox("sponsorIdFileId", "Sponsor Identity Document")}
                    </div>
                  </div>
                )}

                {/* Step 5: Visa Support */}
                {currentStep === 5 && (
                  <div className="space-y-6">
                    <div className="pb-4 border-b border-gray-200">
                      <h3 className="text-xl font-bold text-gray-900 mb-1">
                        Visa Support Documents
                      </h3>
                      <p className="text-gray-600 text-sm">
                        Mandatory and supporting files for UK student visa approval.
                      </p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      {renderUploadBox("passportFullPageFileId", "International Passport (All Pages)")}
                      {renderUploadBox("proofOfFundsFileId", "Proof of Funds")}
                      {renderUploadBox("tbTestCertFileId", "Tuberculosis Test Certificate", false)}
                      {renderUploadBox("policeClearanceCertFileId", "Police Clearance Certificate", false)}
                      {renderUploadBox("marriageCertFileId", "Marriage Certificate", false)}
                      {renderUploadBox("relationshipProofFileId", "Dependent Relationship Documents", false)}
                      {renderUploadBox("dependentBirthCertFileId", "Dependent Birth Certificate", false)}
                      {renderUploadBox("nameDiscrepancyAffidavitFileId", "Name Discrepancy Affidavit", false)}
                    </div>
                  </div>
                )}

                {/* Step 6: Consent */}
                {currentStep === 6 && (
                  <div className="space-y-6">
                    <div className="pb-4 border-b border-gray-200">
                      <h3 className="text-xl font-bold text-gray-900 mb-1">
                        Declarations & Consents
                      </h3>
                      <p className="text-gray-600 text-sm">
                        Please review and accept these terms to submit your application.
                      </p>
                    </div>

                    <div className="space-y-4">
                      <label className="flex items-start space-x-3 p-4 bg-gray-50 border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-100 transition-colors">
                        <input
                          type="checkbox"
                          checked={formData.consent.confirmAll}
                          onChange={(e) => handleInputChange("consent", "confirmAll", e.target.checked)}
                          className="w-5 h-5 rounded border-gray-300 text-yellow-500 focus:ring-yellow-500 mt-0.5 shrink-0"
                          required
                        />
                        <div>
                          <span className="text-sm font-semibold text-gray-900 block">Declaration of Accuracy</span>
                          <span className="text-gray-600 text-sm block mt-1">
                            I declare that all details in this application are correct and verified. I understand that submitting false documents will result in application rejection.
                          </span>
                        </div>
                      </label>

                      <label className="flex items-start space-x-3 p-4 bg-gray-50 border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-100 transition-colors">
                        <input
                          type="checkbox"
                          checked={formData.consent.consentUseDocuments}
                          onChange={(e) => handleInputChange("consent", "consentUseDocuments", e.target.checked)}
                          className="w-5 h-5 rounded border-gray-300 text-yellow-500 focus:ring-yellow-500 mt-0.5 shrink-0"
                          required
                        />
                        <div>
                          <span className="text-sm font-semibold text-gray-900 block">Consent to Share Documents</span>
                          <span className="text-gray-600 text-sm block mt-1">
                            I authorize Educential to share these documents with partner universities and admission organizations to process my study offers.
                          </span>
                        </div>
                      </label>
                    </div>
                  </div>
                )}

                {/* Navigation Controls */}
                <div className="flex space-x-4 pt-6 border-t border-gray-200">
                  {currentStep > 1 && (
                    <button
                      type="button"
                      onClick={handlePrev}
                      className="flex-1 py-3 bg-white hover:bg-gray-50 border border-gray-300 text-gray-700 rounded-lg text-sm font-semibold transition-colors flex items-center justify-center space-x-2"
                    >
                      <ArrowLeft className="w-4 h-4" />
                      <span>Previous</span>
                    </button>
                  )}

                  {currentStep < 6 ? (
                    <button
                      type="button"
                      onClick={handleNext}
                      className="flex-1 py-3 bg-yellow-500 hover:bg-yellow-600 text-white rounded-lg text-sm font-semibold transition-colors flex items-center justify-center space-x-2"
                    >
                      <span>Continue</span>
                      <ArrowRight className="w-4 h-4" />
                    </button>
                  ) : (
                    <button
                      type="submit"
                      onClick={handleSubmit}
                      disabled={submitting || !isStepValid()}
                      className="flex-1 py-3 bg-yellow-500 hover:bg-yellow-600 disabled:bg-gray-300 disabled:cursor-not-allowed text-white rounded-lg text-sm font-semibold transition-colors flex items-center justify-center space-x-2"
                    >
                      {submitting ? (
                        <div className="w-5 h-5 rounded-full border-2 border-white/30 border-t-white animate-spin"></div>
                      ) : (
                        <>
                          <span>Submit Application</span>
                          <CheckCircle2 className="w-4 h-4" />
                        </>
                      )}
                    </button>
                  )}
                </div>

              </form>
            </div>
          </div>
        </div>
      </div>

      <UploadModal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        onUploadSuccess={handleUploadSuccess}
        title={modalTitle}
      />
    </div>
  );
}
